import React, { useState, useEffect } from 'react';

/**
 * Enhanced PaymentStatus component optimized for immediate success display
 */
const PaymentStatus = ({ 
  status, 
  message, 
  theme = 'light',
  // Props for success screen
  payment = null,
  transactionHash = null,
  selectedPaymentMethod = null,
  merchantName = 'Merchant',
  onClose = null, // The onClose prop for the close button
  // Processing info
  processingStartTime = null,
  paymentNetwork = null,
  pendingBackendConfirmation = false // New prop to show backend confirmation status
}) => {
  const [copiedHash, setCopiedHash] = useState(false);
  const [processingSeconds, setProcessingSeconds] = useState(0);
  const [showEarlySuccessOption, setShowEarlySuccessOption] = useState(false);
  const [progressStage, setProgressStage] = useState(1); // 1: Submitted, 2: Detected, 3: Confirming

  // Handle processing timer and stages
  useEffect(() => {
    let timer;
    if (status === 'processing' && processingStartTime) {
      // Update processing time every second
      timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - processingStartTime) / 1000);
        setProcessingSeconds(elapsed);
        
        // Update progress stages based on time
        if (elapsed >= 5 && progressStage === 1) {
          setProgressStage(2); // Transaction detected
        }
        if (elapsed >= 15 && progressStage === 2) {
          setProgressStage(3); // Confirming transaction
        }
        
        // Show early success option after 30 seconds
        if (elapsed >= 30 && !showEarlySuccessOption) {
          setShowEarlySuccessOption(true);
        }
      }, 1000);
    }
    
    return () => clearInterval(timer);
  }, [status, processingStartTime, progressStage, showEarlySuccessOption]);

  // Copy transaction hash to clipboard
  const copyTransactionHash = async () => {
    if (transactionHash) {
      try {
        await navigator.clipboard.writeText(transactionHash);
        setCopiedHash(true);
        setTimeout(() => setCopiedHash(false), 2000);
      } catch (err) {
        console.error('Failed to copy transaction hash:', err);
      }
    }
  };

  // Handle close button click
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  // Format transaction hash for display
  const formatTransactionHash = (hash) => {
    if (!hash) return '';
    if (hash.length <= 16) return hash;
    return `${hash.slice(0, 8)}...${hash.slice(-8)}`;
  };

  // Get network display name
  const getNetworkDisplayName = (network) => {
    const names = {
      ethereum: 'Ethereum',
      bsc: 'BSC',
      tron: 'TRON',
      algorand: 'Algorand'
    };
    return names[network] || network;
  };

  // Format recipient address
  const formatRecipientAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length <= 16) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Render different icons based on status
  const renderIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-purple-600 border-blue-500/20"></div>
        );
      case 'success':
        return (
          <div className="rounded-full h-16 w-16 bg-green-500 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  // Get status title
  const getStatusTitle = () => {
    switch (status) {
      case 'processing':
        return 'Processing Payment';
      case 'success':
        return 'Payment Successful!';
      case 'error':
        return 'Payment Failed';
      default:
        return 'Unknown Status';
    }
  };

  // Get appropriate text color for message
  const getMessageClasses = () => {
    switch (status) {
      case 'processing':
        return theme === 'dark' ? 'text-blue-300' : 'text-blue-600';
      case 'success':
        return theme === 'dark' ? 'text-green-300' : 'text-green-600';
      case 'error':
        return theme === 'dark' ? 'text-red-300' : 'text-red-600';
      default:
        return theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
    }
  };

  // Enhanced success screen
  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-6 px-4">
        <div className="mb-6">{renderIcon()}</div>
        
        <h3 className={`text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Payment Successful!
        </h3>
        
        {payment && selectedPaymentMethod && (
          <p className={`text-center max-w-[235px] mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Your payment of <span className='text-[#7042D2] font-semibold'>{payment.totalAmount || payment.amount} {selectedPaymentMethod.currency}</span> has been completed.
          </p>
        )}

        {/* Subtle backend confirmation indicator */}
        {pendingBackendConfirmation && (
          <div className="flex items-center justify-center mb-4 text-xs text-gray-500 bg-gray-50 rounded-full px-3 py-1">
            <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Verifying on blockchain
          </div>
        )}

        {/* Transaction Hash */}
        {transactionHash && (
          <div className="w-full mb-6 px-4">
            <p className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              Transaction Hash:
            </p>
            <div 
              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
              onClick={copyTransactionHash}
            >
              <span className={`font-mono text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatTransactionHash(transactionHash)}
              </span>
              <div className="flex items-center">
                {copiedHash ? (
                  <span className="text-green-500 text-xs font-medium mr-2">Copied!</span>
                ) : null}
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Payment Details Box */}
        <div className={`w-full p-4 rounded-lg border mb-6 ${
          theme === 'dark' 
            ? 'bg-gray-700 border-gray-600' 
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Merchant ID:
              </p>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {merchantName}
              </p>
            </div>
            <div>
              <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Network:
              </p>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {selectedPaymentMethod ? getNetworkDisplayName(selectedPaymentMethod.network) : 'N/A'}
              </p>
            </div>
            <div className="col-span-2">
              <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Recipient:
              </p>
              <p className={`text-sm font-mono ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {payment?.recipientWallet ? formatRecipientAddress(payment.recipientWallet) : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Close Button - Ensure it calls onClose */}
        {onClose && (
          <button
            onClick={handleClose}
            className="w-full py-3 px-4 bg-[#7042D2] text-white font-medium rounded-2xl text-lg shadow-md hover:bg-[#5b34b1] transition-colors"
            style={{ borderRadius: '1.5rem' }}
          >
            Close
          </button>
        )}
      </div>
    );
  }

  // Error screen
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center py-6">
        <div className="mb-4">{renderIcon()}</div>
        <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Payment Failed
        </h3>
        <p className={`text-center ${getMessageClasses()}`}>{message}</p>

        <div className={`mt-4 p-2 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            Please try again or contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  // Default fallback for processing or other states
  return (
    <div className="flex flex-col items-center justify-center py-6">
      <div className="mb-4">{renderIcon()}</div>
      <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
        {getStatusTitle()}
      </h3>
      <p className={`text-center ${getMessageClasses()}`}>{message}</p>
    </div>
  );
};

export default PaymentStatus;